import { useTranslation } from "react-i18next";
import FileInput from "../../../../components/inputs/fileInput";
import { useFormik } from "formik";
import {
  getContrastingTextColor,
  mode,
  primaryColor,
  secondaryColor,
} from "../../../../utils";
import { useEffect } from "react";
import PaintBoardIcon from "../../../../icons/paintBoardIcon";

const VisualIdentity = () => {
  const { t } = useTranslation();
  const formik = useFormik({
    initialValues: {
      primary: "#0051ff",
      secondary: "#ff0000",
      mode: "light",
    },
  });

  useEffect(() => {
    if (secondaryColor) formik.setFieldValue("secondary", secondaryColor);
    if (primaryColor) formik.setFieldValue("primary", primaryColor);

    if (mode) {
      formik.setFieldValue("mode", mode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrimaryColorChange = (e) => {
    formik.setFieldValue("primary", e.target.value);
  };
  const handleSecondaryColorChange = (e) => {
    formik.setFieldValue("secondary", e.target.value);
  };

  const handleApplyColors = () => {
    localStorage.setItem("primaryColor", formik.values.primary);
    localStorage.setItem("secondaryColor", formik.values.secondary);
    window.location.reload();
  };

  const handleApplyMode = () => {
    if (formik.values.mode === "light") {
      formik.setFieldValue("mode", "dark");
      localStorage.setItem("mode", "dark");
      document.documentElement.classList.add("dark");
    } else {
      formik.setFieldValue("mode", "light");
      localStorage.setItem("mode", "light");
      document.documentElement.classList.remove("dark");
    }
    window.location.reload();
  };

  return (
    <main>
      <section className="flex flex-wrap justify-between gap-5 md:flex-nowrap">
        <div className="w-full p-6 bg-white dark:bg-gray-900 rounded-xl">
          <h1 className="font-bold capitalize dark:text-white">{t("companyLogo")}</h1>
          <FileInput id={"company-logo"} formik={formik} name={"companyLogo"} />
          <p className="bg-[#F3F4F6] dark:bg-gray-800 p-4 rounded-lg text-center dark:text-gray-300">
            {t("logoWillAppearInInterface")}
          </p>
        </div>
        <div className="w-full p-6 bg-white dark:bg-gray-900 rounded-xl">
          <h1 className="font-bold capitalize dark:text-white">{t("printLogo")}</h1>
          <FileInput id={"print-logo"} formik={formik} name={"printLogo"} />
          <p className="bg-[#F3F4F6] dark:bg-gray-800 p-4 rounded-lg text-center dark:text-gray-300">
            {t("logoWillAppearInPrints")}
          </p>
        </div>
      </section>

      <section className="flex flex-wrap justify-between gap-5 my-4 md:flex-nowrap">
        <div className="w-full p-6 bg-white dark:bg-gray-900 rounded-xl">
          <h1 className="font-bold capitalize dark:text-white">
            {t("interfaceColors")}
          </h1>

          {/* Primary Color */}
          <div className="my-10">
            <div className="flex items-center justify-between gap-4 my-2 capitalize text-mainGray dark:text-gray-400">
              <span>{t("primaryColor")}</span>
              <p className="flex items-center gap-2">
                {formik.values.primary}
                <span
                  className="block rounded-full size-6"
                  style={{ backgroundColor: formik.values.primary }}
                />
              </p>
            </div>
            <label
              htmlFor="interface-colors-primary"
              className="relative block w-full h-6 rounded-lg"
              style={{ backgroundColor: formik.values.primary }}
            >
              <input
                type="color"
                id="interface-colors-primary"
                className="absolute top-0 left-0 w-0 h-0"
                onChange={handlePrimaryColorChange}
              />
            </label>
          </div>

          {/* Secondary Color */}
          <div>
            <div className="flex items-center justify-between gap-4 my-2 capitalize text-mainGray dark:text-gray-400">
              <span>{t("secondaryColor")}</span>
              <p className="flex items-center gap-2">
                {formik.values.secondary}
                <span
                  className="block rounded-full size-6"
                  style={{ backgroundColor: formik.values.secondary }}
                />
              </p>
            </div>
            <label
              htmlFor="interface-colors-secondary"
              className="relative block w-full h-6 rounded-lg"
              style={{ backgroundColor: formik.values.secondary }}
            >
              <input
                type="color"
                id="interface-colors-secondary"
                className="absolute top-0 left-0 w-0 h-0"
                onChange={handleSecondaryColorChange}
              />
            </label>
          </div>

          <button
            onClick={handleApplyColors}
            className="w-full py-2 my-10 text-center rounded-lg"
            style={{
              backgroundColor: primaryColor,
              color: getContrastingTextColor(primaryColor),
            }}
          >
            {t("applyColors")}
          </button>
        </div>

        {/* Display Mode Switcher */}
        <div className="w-full p-6 bg-white dark:bg-gray-900 rounded-xl">
          <h1 className="font-bold capitalize dark:text-white">{t("displayMode")}</h1>
          <div className="bg-[#F3F4F6] dark:bg-gray-800 p-4 rounded-2xl flex justify-between gap-4 flex-wrap md:flex-nowrap">
            <div className="flex items-center gap-3">
              <span className="grid rounded-full size-10 place-content-center">
                <PaintBoardIcon fill={primaryColor} />
              </span>
              <div>
                <h1 className="font-medium dark:text-white">{t(mode)}</h1>
                <p className="text-mainGray dark:text-gray-400">
                  {mode === "light" ? t("dayUse") : t("nightUse")}
                </p>
              </div>
            </div>
            <div>
              <button
                onClick={handleApplyMode}
                className="w-full px-2 py-2 my-10 text-center rounded-lg"
                style={{
                  backgroundColor: primaryColor,
                  color: getContrastingTextColor(primaryColor),
                }}
              >
                {formik.values.mode === "light"
                  ? t("enableDarkMode")
                  : t("enableLightMode")}
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default VisualIdentity;
